import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css'; // Import Swiper styles
import './style.css'; // Import your custom CSS
// routes

// @types
import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';

import { IContractType } from 'src/@types/foamCompanyTypes/CategoryType';
import { useStore } from 'src/stores/store';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { Add, Remove } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit'; // Importing the table icon
// ----------------------------------------------------------------------

export default observer(function ContractTypeNewEditForm() {
  const { ContractTypeStore } = useStore();
  const { translate } = useLocales();
  const {
    createContractType,
    updateContractType,
    editMode,
    selectedContractType,
    clearSelectedContractType,
  } = ContractTypeStore;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewContractTypeSchema = Yup.object().shape({
    englishName: Yup.string().required(`${translate('Validation.EnglishName')}`),
    pashtoName: Yup.string().required(`${translate('Validation.PashtoName')}`),
    dariName: Yup.string().required(`${translate('Validation.DariName')}`),
    code: Yup.string().required(`${translate('Validation.code')}`),
  });

  const defaultValues = useMemo<IContractType>(
    () => ({
      id: selectedContractType?.id,
      englishName: selectedContractType?.englishName || '',
      dariName: selectedContractType?.dariName || '',
      pashtoName: selectedContractType?.pashtoName || '',
      code: selectedContractType?.code || '',
    }),
    [selectedContractType]
  );

  const methods = useForm<IContractType>({
    resolver: yupResolver(NewContractTypeSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = (data: IContractType) => {
    if (data.id! === undefined) {
      ///create
      createContractType(data).then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        navigate(PATH_DASHBOARD.ContractType.list);
      });
    } else {
      ///update
      updateContractType(data).then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
        navigate(PATH_DASHBOARD.ContractType.list);
      });
    }
  };

  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);
  const categoryitems = [
    {
      id: 1,
      name: 'Fruits',
      image: '/menu/fruite.jpeg', // Fruits image
      items: [
        {
          id: 101,
          name: 'Apple',
          price: 1.5,
          description: 'A sweet and crunchy fruit.',
          image: '/menu/Apple.avif', // Apple image
        },
        {
          id: 102,
          name: 'Banana',
          price: 1.0,
          description: 'A soft, yellow fruit rich in potassium.',
          image: '/menu/Banana.jpg', // Banana image
        },
        {
          id: 103,
          name: 'Mango',
          price: 2.0,
          description: 'A tropical fruit with a sweet and juicy flavor.',
          image: '/menu/Mango.jpg', // Mango image
        },
        {
          id: 104,
          name: 'Pineapple',
          price: 3.0,
          description: 'A tropical fruit with a tangy and sweet taste.',
          image: '/menu/Pineapple.jpg', // Pineapple image
        },
      ],
    },
    {
      id: 2,
      name: 'Vegetables',
      image: '/menu/vegetable.jpeg', // Vegetables image
      items: [
        {
          id: 201,
          name: 'Carrot',
          price: 0.5,
          description: 'A crunchy, orange vegetable rich in vitamins.',
          image: '/menu/Carrot.jpg', // Carrot image
        },
        {
          id: 202,
          name: 'Broccoli',
          price: 1.2,
          description: 'A green vegetable packed with nutrients.',
          image: '/menu/Broccoli.jpg', // Broccoli image
        },
        {
          id: 203,
          name: 'Spinach',
          price: 1.5,
          description: 'A leafy green vegetable rich in iron.',
          image: '/menu/Spinach.jpg', // Spinach image
        },
        {
          id: 204,
          name: 'Potato',
          price: 0.8,
          description: 'A starchy vegetable commonly used in many dishes.',
          image: '/menu/Potato.jpg', // Potato image
        },
      ],
    },
    {
      id: 3,
      name: 'Dairy',
      image: '/menu/diary.jpg', // Dairy image
      items: [
        {
          id: 301,
          name: 'Milk',
          price: 1.0,
          description: 'A nutritious liquid produced by mammals.',
          image: '/menu/Milk.jpg', // Milk image
        },
        {
          id: 302,
          name: 'Cheese',
          price: 2.5,
          description: 'A dairy product made from curdled or cultured milk.',
          image: '/menu/Cheese.jpg', // Cheese image
        },
        {
          id: 303,
          name: 'Yogurt',
          price: 1.2,
          description: 'A creamy dairy product made by fermenting milk.',
          image: '/menu/Yogurt.jpg', // Yogurt image
        },
      ],
    },
    {
      id: 4,
      name: 'Meat',
      image: '/menu/meat.jpeg', // Meat image
      items: [
        {
          id: 401,
          name: 'Chicken',
          price: 5.0,
          description: 'A popular poultry meat.',
          image: '/menu/Chicken.jpg', // Chicken image
        },
        {
          id: 402,
          name: 'Beef',
          price: 7.0,
          description: 'A rich source of protein from cattle.',
          image: '/menu/Beef.jpg', // Beef image
        },
        {
          id: 403,
          name: 'Lamb',
          price: 8.0,
          description: 'Tender meat from young sheep.',
          image: '/menu/Lamb.jpg', // Lamb image
        },
      ],
    },
    {
      id: 5,
      name: 'Grains',
      image: '/menu/grain.jpeg', // Grains image
      items: [
        {
          id: 501,
          name: 'Rice',
          price: 1.0,
          description: 'A staple food in many cultures, high in carbohydrates.',
          image: '/menu/Rice.jpg', // Rice image
        },
        {
          id: 502,
          name: 'Wheat',
          price: 0.8,
          description: 'A common grain used for making bread and pasta.',
          image: '/menu/Wheat.avif', // Wheat image
        },
      ],
    },
    {
      id: 6,
      name: 'Beverages',
      image: '/menu/Beverages.jpg', // Beverages image
      items: [
        {
          id: 601,
          name: 'Tea',
          price: 1.5,
          description: 'A fragrant beverage made from steeped tea leaves.',
          image: '/menu/Tea.jpg', // Tea image
        },
        {
          id: 602,
          name: 'Coffee',
          price: 2.0,
          description: 'A popular caffeinated drink made from roasted coffee beans.',
          image: '/menu/Coffee.jpg', // Coffee image
        },
      ],
    },
  ];

  // State to store the selected category ID
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(undefined);
  const [isFading, setIsFading] = useState(false); // Control the fade-in effect
  const [open, setOpen] = useState(false);
  const [selectedSize, setSelectedSize] = useState('medium');
  const [itemCounts, setItemCounts] = useState<Record<number, number>>({});
  const [currentItemId, setCurrentItemId] = useState<number | null>(null);
  const [cartItems, setCartItems] = useState<any[]>([]); // Store all selected menu items here
  const [value, setValue] = useState<string | null>('default'); // this is for toggle button
  const [tabvalue, setTabValue] = useState(2);
  const [openDrawer, setOpenDrawer] = useState(false); //for drawer
  const [selectedHall, setSelectedHall] = useState<Hall | null>(null); // Correct type
  const [selectedTable, setSelectedTable] = useState<Table | null>(null); // Correct type
  const [activeButton, setActiveButton] = useState<string | null>('Din in'); // for buttongroup

  const handleButtonClick = (button: string) => {
    // for button group
    setActiveButton(button);
  };

  interface Table {
    id: number;
    name: string;
  }

  interface Hall {
    id: number;
    name: string;
    tables: Table[];
  }
  const halls = [
    {
      id: 1,
      name: 'Hall A',
      tables: [
        { id: 101, name: 'Table 1' },
        { id: 102, name: 'Table 2' },
      ],
    },
    {
      id: 2,
      name: 'Hall B',
      tables: [
        { id: 201, name: 'Table 1' },
        { id: 202, name: 'Table 2' },
      ],
    },
  ];
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpenDrawer(newOpen);
  };

  const handleHallClick = (hall: Hall) => {
    setSelectedHall(hall);
    setSelectedTable(null); // Reset the selected table when switching halls
  };

  const handleTableClick = (table: { id: number; name: string }) => {
    setOpenDrawer(false);
    setSelectedTable(table);
  };

  // Find the selected category's items or show all items initially
  const filteredItems = selectedCategory
    ? categoryitems.find((category) => category.id === selectedCategory)?.items ?? []
    : [];

  // Trigger fade-out and then fade-in when category changes
  useEffect(() => {
    setIsFading(true); // Start fade-out
    const timeout = setTimeout(() => setIsFading(false), 300); // Fade-in after 300ms
    return () => clearTimeout(timeout); // Clean up timeout
  }, [selectedCategory]);

  // Open the modal
  const handleAddToDishes = (id: number) => {
    setCurrentItemId(id); // Save current item ID
    setOpen(true); // Open the modal
  };

  // Close the modal
  const handleClose = () => {
    if (currentItemId !== null) {
      setCartItems((prevItems) => {
        // Check if an item with the same ID and size exists
        const existingItemIndex = prevItems.findIndex(
          (item) => item.id === currentItemId && item.size === selectedSize
        );

        if (existingItemIndex >= 0) {
          // If the item with the same size exists, update its quantity
          const updatedItems = [...prevItems];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: updatedItems[existingItemIndex].quantity + (itemCounts[currentItemId] || 1),
          };
          return updatedItems;
        }

        // If the item doesn't exist with the same size, add it as a new order item
        const currentItem = filteredItems.find((item) => item.id === currentItemId); // Get the current item
        return [
          ...prevItems,
          {
            id: currentItemId,
            name: currentItem?.name,
            size: selectedSize,
            quantity: itemCounts[currentItemId] || 1,
            image: currentItem?.image || '/default-image.jpg', // Add the image URL or default image
            price: currentItem?.price,
          },
        ];
      });
    }

    setOpen(false); // Close modal
  };

  // Handle size change
  const handleSizeChange = (event: SelectChangeEvent<string>) => {
    setSelectedSize(event.target.value as string);
  };

  // Increment item count
  const handleIncrement = (id: number) => {
    setItemCounts((prevCounts) => ({
      ...prevCounts,
      [id]: (prevCounts[id] || 0) + 1,
    }));
  };

  // Decrement item count
  const handleDecrement = (id: number) => {
    setItemCounts((prevCounts) => ({
      ...prevCounts,
      [id]: Math.max((prevCounts[id] || 1) - 1, 1),
    }));
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Categories Menu */}
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Categories
                </Typography>
                <Swiper
                  spaceBetween={8}
                  slidesPerView={5}
                  pagination={{ clickable: true }}
                  breakpoints={{
                    640: {
                      slidesPerView: 1,
                    },
                    768: {
                      slidesPerView: 2,
                    },
                    1024: {
                      slidesPerView: 5,
                    },
                  }}
                >
                  {categoryitems.map((category, index) => (
                    <SwiperSlide key={index}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          borderRadius: '12px',
                          overflow: 'hidden',
                        }}
                        onClick={() => setSelectedCategory(category.id)}
                      >
                        <CardMedia
                          component="img"
                          alt={category.name}
                          height="140"
                          image={category.image}
                        />
                        <div className="menu-overlay">
                          <Typography variant="body2" className="overlay-text">
                            {category.name}
                          </Typography>
                        </div>
                      </Card>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Card>
            </Grid>

            {/* Menu Items with Transition */}
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Menu Items
                </Typography>
                <Grid container spacing={2} className={isFading ? 'fade-out' : 'fade-in'}>
                  {filteredItems.length > 0 ? (
                    filteredItems.map((menuItem, index) => (
                      <Grid item key={index} xs={2}>
                        <Card
                          sx={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          <CardMedia
                            component="img"
                            sx={{ height: 140 }}
                            image={menuItem.image}
                            title={menuItem.name}
                          />
                          <div className="menu-overlay">
                            <Typography variant="body2" className="overlay-text">
                              {menuItem.name}
                            </Typography>
                          </div>
                          <CardContent>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              ${menuItem.price.toFixed(2)}
                            </Typography>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'center' }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              fullWidth
                              onClick={() => handleAddToDishes(menuItem.id)}
                            >
                              Add to Dishes
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', width: '100%' }}>
                      Select a category to see items.
                    </Typography>
                  )}
                </Grid>
              </Card>

              {/* Modal for selecting meal size and count */}
              <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Select Size and Quantity</DialogTitle>
                <DialogContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Select Size:
                  </Typography>
                  <Select value={selectedSize} onChange={handleSizeChange} fullWidth sx={{ mb: 3 }}>
                    <MenuItem value="small">Small</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="large">Large</MenuItem>
                  </Select>

                  <Typography variant="subtitle1" gutterBottom>
                    Quantity:
                  </Typography>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: '1px solid #1976d2',
                      borderRadius: '10px',
                      padding: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                    }}
                  >
                    <IconButton color="primary" onClick={() => handleDecrement(currentItemId!)}>
                      <Remove />
                    </IconButton>
                    <Typography sx={{ mx: 1 }}>{itemCounts[currentItemId!] || 1}</Typography>
                    <IconButton color="primary" onClick={() => handleIncrement(currentItemId!)}>
                      <Add />
                    </IconButton>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={handleClose} color="primary">
                    Add to Cart
                  </Button>
                </DialogActions>
              </Dialog>
            </Grid>
          </Grid>
        </Grid>

        {/* SideNave start            */}
        <Grid item xs={12} md={4} sx={{ height: '100vh' }}>
          <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid gray',
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {selectedTable?.name}
                </Typography>
                <IconButton onClick={toggleDrawer(true)}>
                  <EditIcon /> {/* Icon representing tables */}
                </IconButton>
              </div>
              <Drawer anchor="right" open={openDrawer} onClose={toggleDrawer(false)}>
                <div style={{ padding: '20px', width: '400px' }}>
                  <h3>Restaurant Halls</h3>
                  {halls.map((hall) => (
                    <Button key={hall.id} onClick={() => handleHallClick(hall)}>
                      {hall.name}
                    </Button>
                  ))}

                  {selectedHall && (
                    <div style={{ marginTop: '20px' }}>
                      <h4>{selectedHall.name} Tables</h4>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyItems: 'flex-start',
                          gap: '10px',
                          marginTop: '20px',
                        }}
                      >
                        {selectedHall.tables.map((table) => (
                          <Box
                            key={table.id}
                            style={{
                              marginBottom: '10px',
                              cursor: 'pointer',
                              padding: '10px',
                              backgroundColor: 'gray',
                              borderRadius: '10px',
                            }}
                            onClick={() => handleTableClick(table)}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'bold',
                                fontSize: {
                                  xs: '12px', // Small devices
                                  sm: '14px', // Tablets
                                  md: '16px', // Medium devices
                                  lg: '18px', // Larger screens
                                },
                              }}
                            >
                              {table.name}
                            </Typography>
                          </Box>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Drawer>
            </div>
            <Box sx={{ mt: 2 }}>
              <ButtonGroup
                fullWidth
                variant="outlined"
                color="inherit"
                aria-label="Basic button group"
              >
                <Button
                  onClick={() => handleButtonClick('Din in')}
                  variant={activeButton === 'Din in' ? 'contained' : 'outlined'}
                >
                  Din in
                </Button>
                <Button
                  onClick={() => handleButtonClick('Take Away')}
                  variant={activeButton === 'Take Away' ? 'contained' : 'outlined'}
                >
                  Take Away
                </Button>
                <Button
                  onClick={() => handleButtonClick('Delivery')}
                  variant={activeButton === 'Delivery' ? 'contained' : 'outlined'}
                >
                  Delivery
                </Button>
              </ButtonGroup>
            </Box>

            <Box sx={{ mt: 2, flexGrow: 1, overflowY: 'auto' }}>
              {cartItems.length > 0 ? (
                cartItems.map((item, index) => (
                  <Card
                    key={index}
                    sx={{
                      mb: 2,
                      borderRadius: '12px',
                      padding: '5px',
                      border: '.5px solid rgba(255,255,255,.3)',
                      marginTop: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        direction: 'row',
                      }}
                    >
                      <img
                        src={item.image} // Assuming `item.image` contains the URL of the image
                        alt={`Image of ${item.id}`}
                        style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '12px',
                          marginRight: '16px', // Adds spacing between image and text
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {`${item.name}  (${item.size})`}
                        </Typography>

                        <Typography variant="body2">
                          <span style={{ color: 'green', fontWeight: 'bold' }}>{item.price}$ </span>{' '}
                          &nbsp; &nbsp; {item.quantity}x
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          fontSize: '14px',
                          position: 'absolute',
                          right: '20px',
                        }}
                      >
                        <Typography variant="body2">Price: {item.price}</Typography>
                        <Typography variant="body2">
                          <span style={{ color: 'green', fontWeight: 'bold' }}>
                            Total price: {(item.quantity * item.price).toFixed()}
                          </span>
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  No items selected.
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                justifyItems: 'center',
                mt: 2,
                mb: 2,
                borderBottom: '1px solid rgba(255,255,255,.2)',
              }}
            >
              <Typography variant="body2">Sub Total:</Typography>
              <Typography variant="body2">600$</Typography>
            </Box>

            <Box sx={{ marginTop: 'auto' }}>
              <Button variant="contained" fullWidth color="inherit">
                Save Order
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* SideNave end            */}
      </Grid>
    </FormProvider>
  );
});
